import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { getComicById, getRecommendedComics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('chapters');
  const [chapterRange, setChapterRange] = useState<'all' | 'latest'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const comic = getComicById(id || '');
  const recommended = getRecommendedComics().filter((c) => c.id !== id).slice(0, 6);

  if (!comic) {
    return (
      <>
        <SEOHead
          title="漫画未找到 - 51漫画"
          description="该漫画不存在或已被移除。来51漫画发现更多精彩免费漫画吧！"
          canonicalUrl={`https://www.51manga.com/article/${id}`}
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 pb-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
              <i className="ri-error-warning-line text-6xl text-foreground-300 mb-4 block"></i>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
              <p className="text-foreground-500 mb-6">该漫画不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const displayedChapters = chapterRange === 'latest'
    ? comic.chapters.slice(-5)
    : comic.chapters;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 免费在线漫画阅读 | 51漫画`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[0]?.updatedAt,
    dateModified: comic.updatedAt,
    publisher: { '@type': 'Organization', name: '51漫画', url: 'https://www.51manga.com/' },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 51漫画在线观看 | 免费漫画全集在线阅读`}
        description={`${comic.title}：${comic.description.slice(0, 120)}。在51漫画免费在线阅读${comic.title}全集，下拉式阅读体验，无需下载，支持网页观看。`}
        keywords={`${comic.title},51漫画,免费漫画,在线漫画,${comic.category}漫画,下拉式阅读`}
        canonicalUrl={`https://www.51manga.com/article/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
                  { label: comic.title },
                ]}
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-8 mb-10">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-[220px] md:w-[260px] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 免费在线漫画阅读`}
                    className="w-full object-cover object-top aspect-[3/4]"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <span className="text-sm text-foreground-500">
                    作者：<strong className="text-foreground-700">{comic.author}</strong>
                  </span>
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-accent-100 text-accent-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-500">
                    分类：<Link to={`/category/${comic.categorySlug}`} className="text-primary-500 hover:text-primary-600 cursor-pointer">{comic.category}</Link>
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-5 mb-4">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-yellow-500"></i>
                    <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-600">{(comic.views / 10000).toFixed(0)}万 阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-heart-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-600">{(comic.followers / 10000).toFixed(0)}万 追漫</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-600">更新于 {comic.updatedAt}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <Link
                  to={`/article/${comic.id}?read=1`}
                  className="inline-flex items-center gap-1.5 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </Link>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-6 border-b border-background-200/70 mb-6">
                <button
                  onClick={() => setActiveTab('chapters')}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'chapters'
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  章节列表 ({comic.chapters.length})
                </button>
                <button
                  onClick={() => setActiveTab('info')}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'info'
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  漫画详情
                </button>
              </div>

              {activeTab === 'chapters' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <button
                      onClick={() => setChapterRange('latest')}
                      className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                        chapterRange === 'latest'
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 text-foreground-500 hover:bg-primary-50'
                      }`}
                    >
                      最新章节
                    </button>
                    <button
                      onClick={() => setChapterRange('all')}
                      className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                        chapterRange === 'all'
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 text-foreground-500 hover:bg-primary-50'
                      }`}
                    >
                      全部章节
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {displayedChapters.map((chapter) => (
                      <Link
                        key={chapter.id}
                        to={`/article/${comic.id}?read=1&chapter=${chapter.number}`}
                        className="flex items-center justify-between p-3 bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 rounded-lg transition-all cursor-pointer group"
                      >
                        <span className="text-sm text-foreground-700 group-hover:text-primary-500 truncate whitespace-nowrap">
                          {chapter.title}
                        </span>
                        <span className="text-[10px] text-foreground-400 ml-2 flex-shrink-0">
                          {chapter.pageCount}页
                        </span>
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {activeTab === 'info' && (
                <div className="space-y-4">
                  <div className="p-4 bg-background-100 rounded-lg border border-background-200/70">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-background-100 rounded-lg border border-background-200/70">
                      <p className="text-xs text-foreground-500">总章节</p>
                      <p className="text-lg font-semibold text-foreground-900">{comic.chapters.length} 话</p>
                    </div>
                    <div className="p-4 bg-background-100 rounded-lg border border-background-200/70">
                      <p className="text-xs text-foreground-500">总阅读</p>
                      <p className="text-lg font-semibold text-foreground-900">{(comic.views / 10000).toFixed(0)} 万</p>
                    </div>
                    <div className="p-4 bg-background-100 rounded-lg border border-background-200/70">
                      <p className="text-xs text-foreground-500">追漫人数</p>
                      <p className="text-lg font-semibold text-foreground-900">{(comic.followers / 10000).toFixed(0)} 万</p>
                    </div>
                    <div className="p-4 bg-background-100 rounded-lg border border-background-200/70">
                      <p className="text-xs text-foreground-500">评分</p>
                      <p className="text-lg font-semibold text-foreground-900">{comic.rating} / 10</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {recommended.length > 0 && (
              <section>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                  猜你喜欢
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {recommended.map((c) => (
                    <ComicCard key={c.id} comic={c} size="medium" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}